import React from "react";
import { stylex } from "./stylex";

interface ContainerProps {
  children?: React.ReactNode;
  backgroundColor?: string;
  boxShadow?: string;
  flex?: number;
  maxWidth?: number | string;
  minWidth?: number | string;
  minHeight?: number | string;
  borderRadius?: number | string;
  width?: number | string;
  height?: number | string;
  overflow?: "hidden" | "visible" | "auto";
  opacity?: number;
  borderWidth?: number;
  borderTopWidth?: number;
  borderRightWidth?: number;
  borderLeftWidth?: number;
  borderBottomWidth?: number;
  borderColor?: string;
  borderStyle?: string;
  paddingRight?: number;
  paddingLeft?: number;
  paddingTop?: number;
  paddingBottom?: number;
  paddingVertical?: number;
  paddingHorizontal?: number;
  padding?: number;
  zIndex?: number;
  center?: boolean;
  expanded?: boolean;
  testID?: string;
  className?: string;
}

/**
 * Provides padding, background decorations, border decorations, sizing and other box styles.
 */
export function Container(props: ContainerProps) {
  const {
    children,
    backgroundColor,
    boxShadow,
    width,
    maxWidth,
    flex,
    center,
    expanded,
    minWidth,
    overflow,
    opacity,
    borderWidth,
    borderTopWidth,
    borderRightWidth,
    borderLeftWidth,
    borderBottomWidth,
    borderColor,
    borderStyle,
    borderRadius,
    paddingRight,
    minHeight,
    paddingLeft,
    paddingTop,
    paddingBottom,
    paddingVertical,
    paddingHorizontal,
    padding,
    zIndex,
    height,
    testID,
    className,
  } = props;

  const effectivePadding = getEffectivePadding({
    paddingRight,
    paddingLeft,
    paddingTop,
    paddingBottom,
    paddingVertical,
    paddingHorizontal,
    padding,
  });

  const effectiveBorder = getEffectiveBorder({
    borderWidth,
    borderTopWidth,
    borderRightWidth,
    borderLeftWidth,
    borderBottomWidth,
    borderColor,
    borderStyle,
  });

  const effectiveSize = getEffectiveSize({ width, height, expanded });

  return (
    <div
      data-testid={testID}
      className={className}
      style={{
        ...styles(center && "center"),
        boxSizing: "border-box",
        position: "relative",
        display: "flex",
        flexDirection: "column",
        backgroundColor,
        maxWidth,
        minWidth,
        borderRadius,
        boxShadow,
        opacity,
        flex,
        minHeight,
        overflow,
        width: effectiveSize.width,
        height: effectiveSize.height,
        paddingRight: effectivePadding.paddingRight,
        paddingLeft: effectivePadding.paddingLeft,
        paddingTop: effectivePadding.paddingTop,
        paddingBottom: effectivePadding.paddingBottom,
        borderTopWidth: effectiveBorder.borderTopWidth,
        borderRightWidth: effectiveBorder.borderRightWidth,
        borderLeftWidth: effectiveBorder.borderLeftWidth,
        borderBottomWidth: effectiveBorder.borderBottomWidth,
        borderColor: effectiveBorder.borderColor,
        borderStyle: effectiveBorder.borderStyle,
        zIndex,
      }}
    >
      {children}
    </div>
  );
}

function getEffectiveSize(values: {
  width?: number | string;
  height?: number | string;
  expanded?: boolean;
}) {
  let width;
  let height;

  if (values.expanded) {
    width = "100%";
    height = "100%";
  }

  if (values.width) {
    width = values.width;
  }

  if (values.height) {
    height = values.height;
  }

  return { width, height };
}

function getEffectiveBorder(values: {
  borderWidth?: number;
  borderTopWidth?: number;
  borderRightWidth?: number;
  borderLeftWidth?: number;
  borderBottomWidth?: number;
  borderColor?: string;
  borderStyle?: string;
}): {
  borderTopWidth?: number;
  borderRightWidth?: number;
  borderLeftWidth?: number;
  borderBottomWidth?: number;
  borderColor?: string;
  borderStyle?: string;
} {
  let borderTopWidth;
  let borderRightWidth;
  let borderLeftWidth;
  let borderBottomWidth;
  let borderColor;
  let borderStyle;

  if (values.borderWidth) {
    borderRightWidth = values.borderWidth;
    borderLeftWidth = values.borderWidth;
    borderTopWidth = values.borderWidth;
    borderBottomWidth = values.borderWidth;
  }

  if (values.borderColor) {
    borderColor = values.borderColor;
  }

  if (values.borderLeftWidth) {
    borderLeftWidth = values.borderLeftWidth;
  }

  if (values.borderTopWidth) {
    borderTopWidth = values.borderTopWidth;
  }

  if (values.borderRightWidth) {
    borderRightWidth = values.borderRightWidth;
  }

  if (values.borderBottomWidth) {
    borderBottomWidth = values.borderBottomWidth;
  }

  const hasBorderWidth = !!(
    borderRightWidth ||
    borderLeftWidth ||
    borderTopWidth ||
    borderBottomWidth
  );

  if (borderStyle) {
    borderStyle = values.borderStyle;
  } else {
    if (hasBorderWidth) {
      borderStyle = "solid";
    }
  }

  if (hasBorderWidth) {
    borderTopWidth = borderTopWidth ?? 0;
    borderRightWidth = borderRightWidth ?? 0;
    borderLeftWidth = borderLeftWidth ?? 0;
    borderBottomWidth = borderBottomWidth ?? 0;
  }

  return {
    borderTopWidth,
    borderRightWidth,
    borderLeftWidth,
    borderBottomWidth,
    borderColor,
    borderStyle,
  };
}

function getEffectivePadding(values: {
  paddingRight?: number;
  paddingLeft?: number;
  paddingTop?: number;
  paddingBottom?: number;
  paddingVertical?: number;
  paddingHorizontal?: number;
  padding?: number;
}): {
  paddingRight?: number;
  paddingLeft?: number;
  paddingTop?: number;
  paddingBottom?: number;
} {
  let paddingRight;
  let paddingLeft;
  let paddingTop;
  let paddingBottom;

  if (values.padding) {
    paddingRight = values.padding;
    paddingLeft = values.padding;
    paddingTop = values.padding;
    paddingBottom = values.padding;
  }

  if (values.paddingHorizontal) {
    paddingRight = values.paddingHorizontal;
    paddingLeft = values.paddingHorizontal;
  }

  if (values.paddingVertical) {
    paddingTop = values.paddingVertical;
    paddingBottom = values.paddingVertical;
  }

  if (values.paddingLeft) {
    paddingLeft = values.paddingLeft;
  }

  if (values.paddingRight) {
    paddingRight = values.paddingRight;
  }

  if (values.paddingTop) {
    paddingTop = values.paddingTop;
  }

  if (values.paddingBottom) {
    paddingBottom = values.paddingBottom;
  }

  return {
    paddingRight,
    paddingLeft,
    paddingTop,
    paddingBottom,
  };
}

const styles = stylex.create({
  center: {
    justifyContent: "center",
    alignItems: "center",
  },
});
